import {
  describeValue,
  type CheckInTemplate,
  type Entry,
  type Track,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { BackupPrompt } from '../../backup/BackupPrompt.js'
import { Button } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { formatRelativeDay, formatTime } from '../../format/dates.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Home.module.css'

const RECENT_COUNT = 5

/**
 * Where logging starts (T-304).
 *
 * One button per check-in, in the order the user arranged them, so the
 * most-used one can be first. Selecting a check-in and filling its values is
 * the whole interaction — two taps plus the values themselves.
 */
export function HomeScreen() {
  const { repos, clock } = useServices()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<CheckInTemplate[] | null>(null)
  const [tracks, setTracks] = useState<Map<string, Track>>(new Map())
  const [recent, setRecent] = useState<Entry[]>([])

  const reload = useCallback(async () => {
    const [allTemplates, allTracks, entries] = await Promise.all([
      repos.templates.list(),
      repos.tracks.list({ includeArchived: true }),
      repos.entries.recent(RECENT_COUNT),
    ])
    setTemplates(allTemplates)
    setTracks(new Map(allTracks.map((track) => [track.id, track])))
    setRecent(entries)
  }, [repos])

  useEffect(() => {
    void reload()
  }, [reload])

  if (templates === null) {
    return (
      <>
        <h1>Home</h1>
        <p>Loading…</p>
      </>
    )
  }

  const hasTracks = tracks.size > 0
  const now = clock.now()

  return (
    <>
      <h1>Home</h1>

      {/*
        Above the check-in buttons, not below the recent entries.
        This is the screen people open every day, which is what makes it the
        only place a backup reminder can actually be seen — and it renders
        nothing at all until there is something at stake (T-503).
      */}
      <BackupPrompt />

      {!hasTracks ? (
        <EmptyState
          title="Nothing set up yet"
          body="Start by deciding what you want to measure. A track can be a 1–10 scale, a yes/no, or a set of options — anything you want to record over time."
          action={
            <Button variant="primary" onClick={() => void navigate('/tracks')}>
              Set up a track
            </Button>
          }
        />
      ) : (
        <div className={styles['checkins']}>
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className={styles['checkin']}
              onClick={() => void navigate(`/log/${template.id}`)}
            >
              <span className={styles['checkinName']}>{template.name}</span>
              <span className={styles['checkinTracks']}>
                {template.trackIds
                  .map((id) => tracks.get(id)?.name)
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </button>
          ))}

          {/*
            The escape hatch, always available. Not everything worth recording
            fits a check-in someone thought of in advance.
          */}
          <button
            type="button"
            className={styles['adhoc']}
            onClick={() => void navigate('/log')}
          >
            Log anything
          </button>
        </div>
      )}

      {/*
        The way back to setup, from the screen you land on.

        Without it the only links to these screens were inside empty states,
        which disappear as soon as they are no longer empty — so making a second
        check-in meant typing a URL. Small and quiet, because it is not what
        this screen is for, but always present, because "add another check-in"
        is a thought you have here.
      */}
      {hasTracks && (
        <nav className={styles['manage']} aria-label="Set up">
          <button type="button" onClick={() => void navigate('/tracks')}>
            Tracks
          </button>
          <button type="button" onClick={() => void navigate('/templates')}>
            Check-ins
          </button>
          <button type="button" onClick={() => void navigate('/tags')}>
            Tags
          </button>
        </nav>
      )}

      {recent.length > 0 && (
        <section>
          <h2 className={styles['recentTitle']}>Recently recorded</h2>
          <div className={styles['recent']}>
            {recent.map((entry) => (
              <div key={entry.id} className={styles['entry']}>
                <div className={styles['entryHeader']}>
                  <span className={styles['entryTemplate']}>
                    {templates.find((t) => t.id === entry.templateId)?.name ?? 'Logged'}
                  </span>
                  <span className={styles['entryWhen']}>
                    {formatRelativeDay(entry.at, now)} at {formatTime(entry.at)}
                  </span>
                </div>
                <div className={styles['entryValues']}>
                  {entry.measurements.map((measurement) => {
                    const track = tracks.get(measurement.trackId)
                    if (!track) {
                      return null
                    }
                    return (
                      <span key={measurement.trackId} className={styles['entryValue']}>
                        {track.name}{' '}
                        <span className={styles['entryNumber']}>
                          {describeValue(track, measurement.value) ?? '—'}
                        </span>
                      </span>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}
